import type { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

// ============================================
// Contract-based types (from definitions)
// ============================================
export type CustomRule = definitions['presentedCustomRule'];
export type CustomRuleInput = definitions['customRule'];
export type CustomRuleType = definitions['customRule']['type'];
export type CustomRulePurpose = definitions['customRule']['purpose'];
export type PointsType = definitions['customRule']['pointsType'];
export type ProductType = definitions['customRule']['productType'];
export type PageInfo = definitions['pageBasedPaginationStats'];
export type CustomRuleStatusCounts = definitions['customRuleStatusCounts'];
export type LoyaltyTier = definitions['accountSummary']['tier'];
export type PendingPointsStatus = definitions['pendingPoints']['status'];
export type AccountRegion = definitions['accountRegions'];

// ============================================
// Custom Rules API types
// ============================================
export type CustomRuleSearchParams =
  paths['/api/lux-loyalty/custom-rules/search']['post']['requestBody']['content']['application/json'] & {
    status?: 'all' | 'active' | 'scheduled' | 'expired';
  };

export type CustomRuleSearchResponse =
  paths['/api/lux-loyalty/custom-rules/search']['post']['responses']['200']['content']['application/json'] & {
    result: {
      customRules: Array<definitions['presentedCustomRule']>;
      pageInfo: PageInfo;
      counts: CustomRuleStatusCounts;
    };
  };

export type CreateCustomRuleRequestBody =
  paths['/api/lux-loyalty/custom-rules']['post']['requestBody']['content']['application/json'];
export type CreateCustomRuleResponse =
  paths['/api/lux-loyalty/custom-rules']['post']['responses']['201']['content']['application/json'];

export type UpdateCustomRuleRequestBody =
  paths['/api/lux-loyalty/custom-rules/{customRuleId}']['patch']['requestBody']['content']['application/json'];
export type UpdateCustomRuleResponse =
  paths['/api/lux-loyalty/custom-rules/{customRuleId}']['patch']['responses']['200']['content']['application/json'];

// ============================================
// Manual Accruals API types (from contract)
// ============================================
export type ManualAccrualStatus = 'PENDING' | 'PROCESSED' | 'FAILED';

export type ManualAccrualsResponse =
  paths['/api/lux-loyalty/manual-accruals']['get']['responses']['200']['content']['application/json'];

export type ManualAccrualFilterParams = paths['/api/lux-loyalty/manual-accruals']['get']['parameters']['query'];

export type ManualAccrualsRetryRequestBody =
  paths['/api/lux-loyalty/manual-accruals']['put']['requestBody']['content']['application/json'];
export type ManualAccrualsRetryResponse =
  paths['/api/lux-loyalty/manual-accruals']['put']['responses']['200']['content']['application/json'];

// Extract ManualAccrual type from the response
export type ManualAccrual = ManualAccrualsResponse['result'][number];

// Extract nested types from ManualAccrual
export type PendingPoints = NonNullable<ManualAccrual['pendingPoints']>;
export type CustomRuleApplied = PendingPoints['customRulesApplied'][number];

// ============================================
// Accrual Log API types (from contract)
// ============================================

/** Raw response from GET /api/lux-loyalty/manual-accruals/accrual-log */
type RawAccrualLogResponse =
  paths['/api/lux-loyalty/manual-accruals/accrual-log']['get']['responses']['200']['content']['application/json'];

/** Raw result object from the contract (before extending manualAccrual) */
type RawAccrualLogResult = RawAccrualLogResponse['result'];

/** A single IBS accrual attempt for an order item */
export type IbsAccrualLog = RawAccrualLogResult['ibsAccrualLogs'][number];

/**
 * A manual accrual record as returned in the accrual log.
 * Extended with estimated point fields not yet in the contract — remove this intersection
 * once the contract includes these fields.
 */
export type AccrualLogManualAccrual = NonNullable<RawAccrualLogResult['manualAccrual']> & {
  estimatedPoints?: number;
  estimatedBonusPoints?: number;
  estimatedStatusCredits?: number;
  estimatedBonusStatusCredits?: number;
};

/** A points adjustment record (manual adjustments applied to a customer account) */
export type PointsAdjustmentRecord = RawAccrualLogResult['pointsAdjustmentRecords'][number];

/** Combined accrual activity log for an order item (with extended manualAccrual type) */
export type AccrualLogResult = Omit<RawAccrualLogResult, 'manualAccrual'> & {
  manualAccrual?: AccrualLogManualAccrual;
};

/** Full response from GET /api/lux-loyalty/manual-accruals/accrual-log (with extended types) */
export type AccrualLogResponse = Omit<RawAccrualLogResponse, 'result'> & {
  result: AccrualLogResult;
};

// ============================================
// Manual Accrual Status Update types (from contract)
// ============================================

/** Request body for updating a manual accrual status */
export type UpdateManualAccrualStatusRequest =
  paths['/api/lux-loyalty/manual-accruals/{itemId}/status']['patch']['requestBody']['content']['application/json'];

/** Response from updating a manual accrual status */
export type UpdateManualAccrualStatusResponse =
  paths['/api/lux-loyalty/manual-accruals/{itemId}/status']['patch']['responses']['200']['content']['application/json'];

// ============================================
// Calculate Earn API types
// ============================================
export type CalculateEarnRequest =
  paths['/api/lux-loyalty/points/calculate-earn']['post']['requestBody']['content']['application/json'];

export type CalculateEarnResponse =
  paths['/api/lux-loyalty/points/calculate-earn']['post']['responses']['200']['content']['application/json'];

// The valid loyaltyProductType values for the calculate-earn endpoint (narrower than ProductType)
export type CalculateEarnProductType = CalculateEarnRequest['productDetails']['loyaltyProductType'];

// Extract nested types from CalculateEarnResponse
export type PointDetail = CalculateEarnResponse['result']['pointDetails'][number];
export type CustomRuleBreakdown = CalculateEarnResponse['result']['customRulesBreakdown'][number];

// ============================================
// Loyalty Account types (from contract)
// ============================================
export type LoyaltyAccountSummary = definitions['accountSummary'];

export type GetLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account']['get']['responses']['200']['content']['application/json'];

export type CreateLoyaltyAccountRequest =
  paths['/api/lux-loyalty/account']['post']['requestBody']['content']['application/json'];

export type CreateLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account']['post']['responses']['201']['content']['application/json'];

// ============================================
// Promo Campaign types (from contract)
// ============================================
export type PromoCampaignPresenter = definitions['promoCampaignPresenter'];
export type PromoCampaignBody = definitions['promoCampaignBody'];
export type PromoCampaignUpdateBody = definitions['promoCampaignUpdateBody'];
export type PromoCampaignAssignmentStats = definitions['promoCampaignAssignmentStats'];

export type PromoCampaignBenefitCode = PromoCampaignPresenter['benefit'];
export type PromoCampaignRegion = PromoCampaignPresenter['region'];
export type PromoCampaignTier = PromoCampaignPresenter['tiers'][number];

export const PROMO_CAMPAIGN_TIERS: ReadonlyArray<PromoCampaignTier> = [
  'bronze',
  'silver',
  'gold',
  'platinum',
  'diamond',
] as const;

export type PromoCampaignSearchParams =
  paths['/api/lux-loyalty/promo-campaigns/search']['post']['requestBody']['content']['application/json'];

export type PromoCampaignSearchResponse =
  paths['/api/lux-loyalty/promo-campaigns/search']['post']['responses']['200']['content']['application/json'];

export type PromoCampaignCreateResponse =
  paths['/api/lux-loyalty/promo-campaigns']['post']['responses']['201']['content']['application/json'];

export type PromoCampaignCreateRequestBody =
  paths['/api/lux-loyalty/promo-campaigns']['post']['requestBody']['content']['application/json'];

export type PromoCampaignReadResponse =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['get']['responses']['200']['content']['application/json'];

export type PromoCampaignUpdateRequestBody =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['patch']['requestBody']['content']['application/json'];

export type PromoCampaignUpdateResponse =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['patch']['responses']['200']['content']['application/json'];

export type PromoCampaignDeleteResponse =
  paths['/api/lux-loyalty/promo-campaigns/{promoCampaignId}']['delete']['responses']['200']['content']['application/json'];

// ============================================
// Constants (runtime arrays for UI dropdowns)
// ============================================
export const PROMO_CAMPAIGN_BENEFIT_CODES: ReadonlyArray<PromoCampaignBenefitCode> = [
  'airport_transfer',
  'discounted_insurance',
  'early_access',
  'hidden_access',
  'lounge_pass',
  'phone_support',
  'tour_exp_credit',
  'travelshoot_credit',
  'hotel_upgrade',
] as const;

export const PROMO_CAMPAIGN_REGIONS: ReadonlyArray<PromoCampaignRegion> = [
  'AU',
  'CA',
  'CN',
  'FR',
  'DE',
  'HK',
  'IN',
  'ID',
  'IE',
  'IL',
  'IT',
  'JP',
  'KR',
  'MO',
  'MY',
  'NL',
  'NZ',
  'PH',
  'QA',
  'SA',
  'SG',
  'ZA',
  'ES',
  'TW',
  'TH',
  'AE',
  'GB',
  'US',
  'VN',
] as const;

export const PRODUCT_TYPE_OPTIONS: ReadonlyArray<NonNullable<ProductType>> = [
  'hotel_flash',
  'hotel_lpc',
  'hotel_lpp_eps',
  'le_tour',
  'tour_bank',
  'trusted_partner_tour',
  'tour_optional_extra',
  'experience',
  'cruise_group_1',
  'cruise_group_2',
  'cruise_group_3',
  'dom_economy',
  'dom_business',
  'int_economy',
  'int_premium_economy',
  'int_business',
  'int_first',
  'lux_plus',
  'gift_card',
  'car_hire',
  'rental',
  'cfmr',
  'insurance',
  'shop_tier_1',
  'shop_tier_2',
  'shop_tier_3',
  'shop_tier_4',
  'shop_tier_5',
  'shop_shipping',
] as const;

export const CUSTOM_RULE_TYPE_OPTIONS: ReadonlyArray<CustomRuleType> = [
  'BONUS_POINTS',
  'BONUS_POINTS_ONLY',
  'MULTIPLE_ON_BASE_POINTS',
] as const;

export const CUSTOM_RULE_PURPOSE_OPTIONS: ReadonlyArray<CustomRulePurpose> = [
  'ESTIMATION_ONLY',
  'ESTIMATION_AND_EARN',
  'ACCRUAL_OVERRIDE',
] as const;

export const POINTS_TYPE_OPTIONS: ReadonlyArray<PointsType> = [
  'BASE_POINTS',
  'BONUS_POINTS',
  'STATUS_CREDITS',
  'BONUS_STATUS_CREDITS',
] as const;

export const MANUAL_ACCRUAL_STATUS_OPTIONS: ReadonlyArray<ManualAccrualStatus> = [
  'PENDING',
  'PROCESSED',
  'FAILED',
] as const;

// ============================================
// Points Adjustment API types (from contract)
// ============================================
export type PointsAdjustmentRequestBody =
  paths['/api/lux-loyalty/points/adjustments']['post']['requestBody']['content']['application/json'];

export type PointsAdjustmentResponse =
  paths['/api/lux-loyalty/points/adjustments']['post']['responses']['200']['content']['application/json'];

// ============================================
// Tier Change API types (from contract)
// ============================================
export type TierChangeRequestBody =
  paths['/api/lux-loyalty/account/tier-change']['post']['requestBody']['content']['application/json'];

export type TierChangeResponse =
  paths['/api/lux-loyalty/account/tier-change']['post']['responses']['200']['content']['application/json'];

// ============================================
// Claim Campaign types (from contract)
// ============================================
export type ClaimCampaign = definitions['claimCampaignPresenter'];
export type ClaimCampaignStatus = ClaimCampaign['status'];
export type ListCampaignsResponse =
  paths['/api/lux-loyalty/claims/campaigns/all']['get']['responses']['200']['content']['application/json'];

export type AdminSubmitClaimRequestBody =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-claim']['post']['requestBody']['content']['application/json'];

export type AdminSubmitClaimResponse =
  paths['/api/lux-loyalty/claims/campaigns/{campaignId}/admin-claim']['post']['responses']['200']['content']['application/json'];

// ============================================
// Claims UI types
// ============================================
export interface ClaimCsvRow {
  customerId: string;
  code?: string;
}

export interface BulkClaimRowResult {
  customerId: string;
  success: boolean;
  claimRecordId?: string;
  error?: string;
}

export interface BulkClaimResult {
  totalProcessed: number;
  successCount: number;
  failedCount: number;
  results: Array<BulkClaimRowResult>;
}

// Constants
// ============================================
// App Constants
// ============================================
export const MIN_START_DATE = '2025-04-01';
export const MAX_END_DATE = '2099-12-31';
export const DEFAULT_PAGE_SIZE = 10;
